/**
 * The note an email sign-in leaves for the phone card, and the remembered sign-in method.
 *
 * WHY A NOTE, NOT A STEP. The card ("You're signed in. Want to sign in by text next time?") used to
 * be the sign-in form's last step. But many hosts take the form off screen the moment someone is
 * signed in: a members-only space's sign-in page reloads into the space, the apps close their box,
 * the space shell swaps the page. So the card never showed there (Rob, 2026-10-21, on core-team's
 * sign-in page). Now the form only writes this note and gets out of the way, and
 * PhoneCardAfterSignIn, mounted above every app in src/main.jsx, shows the card on whatever page the
 * person lands on.
 *
 * Kept in sessionStorage: it survives the reload a host does in the same tab, and goes with the tab.
 * It is for one account, and it expires, so an old note never surfaces on a later visit.
 *
 * WRITTEN THE MOMENT THE CODE IS ACCEPTED, before the form's next network call: a host that reloads
 * on sign-in does so at once, and a note written after one more await was never written at all. So
 * the card also waits while any sign-in form is still on screen — a new account's required "What's
 * your name?" step must not be covered by it. Forms say they are open with signInFormOpened().
 */
const METHOD_KEY = 'arcos-sign-in-with';
const CARD_KEY = 'arcos-phone-card';
export const CARD_TTL_MS = 15 * 60 * 1000;

const session = () => { try { return window.sessionStorage; } catch { return null; } };
const local = () => { try { return window.localStorage; } catch { return null; } };

/** Which way this device signed in last, so someone who uses text opens on the phone box next time. */
export function lastSignInMethod() {
  try { return local()?.getItem(METHOD_KEY) === 'phone' ? 'phone' : 'email'; } catch { return 'email'; }
}
export function rememberSignInMethod(method) {
  try { local()?.setItem(METHOD_KEY, method); } catch { /* storage off */ }
}

/**
 * Ask for the card after this sign-in.
 * @param {{ accountId: string, phone?: string|null, now?: number }} p
 *   `phone` is the number they typed asking for text (E.164), shown as "We'll text …"; null shows an
 *   empty phone box.
 */
export function queuePhoneCard({ accountId, phone = null, now = Date.now() }) {
  if (!accountId) return;
  try { session()?.setItem(CARD_KEY, JSON.stringify({ accountId, phone, at: now })); } catch { /* storage off */ }
}

/** The card asked for this account, if it is still fresh; null otherwise. */
export function readPhoneCard(accountId, now = Date.now()) {
  if (!accountId) return null;
  let note = null;
  try { note = JSON.parse(session()?.getItem(CARD_KEY) || 'null'); } catch { note = null; }
  if (!note || note.accountId !== accountId || !(now - note.at < CARD_TTL_MS)) return null;
  return note;
}

// How many sign-in forms are on screen, so the card waits for the last to go.
const FORM_EVENT = 'arcos-signin-form';
let openForms = 0;
export function signInFormOpened() {
  openForms += 1;
  try { window.dispatchEvent(new Event(FORM_EVENT)); } catch { /* no window */ }
  return () => {
    openForms = Math.max(0, openForms - 1);
    try { window.dispatchEvent(new Event(FORM_EVENT)); } catch { /* no window */ }
  };
}
export const isSignInFormOpen = () => openForms > 0;
export function onSignInFormChange(cb) {
  try { window.addEventListener(FORM_EVENT, cb); } catch { return () => {}; }
  return () => { try { window.removeEventListener(FORM_EVENT, cb); } catch { /* no window */ } };
}

export function clearPhoneCard() {
  try { session()?.removeItem(CARD_KEY); } catch { /* storage off */ }
}
